import React from 'react';
import { motion } from 'framer-motion';
import { 
  CogIcon, 
  FolderIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface DashboardSwitcherProps {
  currentMode: 'management' | 'browser';
  userRole: 'admin' | 'manager';
  onSwitchToBrowser?: () => void;
  onSwitchToManagement?: () => void;
}

export default function DashboardSwitcher({ 
  currentMode, 
  userRole, 
  onSwitchToBrowser, 
  onSwitchToManagement 
}: DashboardSwitcherProps) {
  const handleSwitch = () => {
    if (currentMode === 'management' && onSwitchToBrowser) {
      onSwitchToBrowser();
    } else if (currentMode === 'browser' && onSwitchToManagement) {
      onSwitchToManagement();
    }
  };

  const getButtonText = () => {
    if (currentMode === 'management') {
      return 'Switch to S3 Browser';
    } else {
      return `Switch to ${userRole === 'admin' ? 'Admin' : 'Manager'} Panel`;
    }
  };

  const getIcon = () => {
    if (currentMode === 'management') {
      return <FolderIcon className="h-4 w-4" />;
    } else {
      return <CogIcon className="h-4 w-4" />;
    }
  };

  return (
    <div className="flex items-center space-x-3">
      {/* Current Mode Indicator */}
      <div className="flex items-center space-x-2 bg-gray-100 px-3 py-1 rounded-full">
        {currentMode === 'management' ? (
          <CogIcon className="h-4 w-4 text-gray-600" />
        ) : (
          <FolderIcon className="h-4 w-4 text-gray-600" />
        )}
        <span className="text-sm text-gray-700 font-medium">
          {currentMode === 'management' 
            ? `${userRole === 'admin' ? 'Admin' : 'Manager'} Panel`
            : 'S3 Browser'
          }
        </span>
      </div>

      {/* Switch Button */}
      <motion.button
        onClick={handleSwitch}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        <ArrowPathIcon className="h-4 w-4" />
        {getIcon()}
        <span>{getButtonText()}</span>
      </motion.button>
    </div>
  );
}